import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { useTranslation, withTranslation } from 'react-i18next';
import { withFormik } from 'formik';
import * as Yup from 'yup';
import injectSheet from 'react-jss';
import { Button, notification } from 'antd';
import { inputProps } from '../../../lib/forms';
import Input from '../../Common/Forms/Input';
import SubmitButton from '../../Common/Forms/SubmitButton';
import { classesPropType } from '../../prop-types';

const VERIFICATION_CHARS = 6;

const styles = {
  button: {
    margin: '25px 0 10px',
  },
  resendButtonContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '5px',
  },
};

function ValidationCodeForm(props) {
  const {
    classes,
    confirmHandler,
    handleSubmit,
    isSubmitting,
    isValid,
    mandatory,
    onSendSMSClick,
    onSkipClick,
    phoneNumber,
    sendingSMS,
  } = props;
  const { t } = useTranslation();

  const skipButton = !mandatory && (
    <Button
      className={classes.button}
      disabled={isSubmitting}
      onClick={onSkipClick}
      block
    >
      {t('phoneValidation.skipButton')}
    </Button>
  );

  if (!confirmHandler) {
    return (
      <>
        <Button
          type="primary"
          disabled={sendingSMS}
          onClick={onSendSMSClick}
          className={classes.button}
          block
        >
          {t('phoneValidation.smsButton')}
        </Button>
        {skipButton}
      </>
    );
  }

  const buttonsDisabled = isSubmitting || sendingSMS;

  return (
    <form onSubmit={handleSubmit}>
      <Input
        {...inputProps('confirmCode', props)}
        label={t('phoneValidation.form.instructions', { length: VERIFICATION_CHARS })}
        inputMode="numeric"
      />
      <SubmitButton
        isSubmitting={buttonsDisabled}
        isValid={isValid}
        title={t('phoneValidation.form.submit')}
        block
      />
      <div className={classes.resendButtonContainer}>
        <Button type="link" disabled={buttonsDisabled} onClick={onSendSMSClick}>
          {t('phoneValidation.form.resendSMS', { phoneNumber })}
        </Button>
      </div>
      {skipButton}
    </form>
  );
}

ValidationCodeForm.propTypes = {
  classes: classesPropType.isRequired,
  confirmHandler: PropTypes.shape({ confirm: PropTypes.func.isRequired }),
  handleSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  isValid: PropTypes.bool.isRequired,
  mandatory: PropTypes.bool.isRequired,
  onSendSMSClick: PropTypes.func.isRequired,
  onSkipClick: PropTypes.func.isRequired,
  phoneNumber: PropTypes.string.isRequired,
  sendingSMS: PropTypes.bool.isRequired,
};

ValidationCodeForm.defaultProps = {
  confirmHandler: undefined,
};

export default compose(
  withTranslation(),
  withFormik({
    mapPropsToValues: () => {},
    validationSchema: ({ t }) => Yup.object().shape({
      confirmCode: Yup.string()
        .required(t('validations.required'))
        .length(VERIFICATION_CHARS, t('validations.digitCodeLength', { length: VERIFICATION_CHARS })),
    }),
    handleSubmit: (
      { confirmCode },
      {
        setSubmitting,
        props: { onSubmit, t },
      },
    ) =>
      onSubmit(confirmCode)
        .catch(() => {
          setSubmitting(false);
          notification.error({
            message: t('phoneValidation.form.error.message'),
            description: t('phoneValidation.form.error.description'),
          });
        }),
  }),
  injectSheet(styles),
)(ValidationCodeForm);
